import React from 'react';
import EmptyState from '../design-system/EmptyState';
import { Activity } from 'lucide-react';
import { getChampionIconUrl } from '../lib/champions';
import { DEATH_TYPES } from '../constants';

/**
 * APEX v8.6 — GameList
 * Liste complète des games (onglet Games).
 * Extrait de App.jsx v8.6 — était inline depuis v5.
 */

const ChampIcon = ({ name, champions, version, size = 32 }) => {
  const champ = champions?.find(c =>
    c.name?.toLowerCase() === name?.toLowerCase() ||
    c.id?.toLowerCase() === name?.toLowerCase()
  );
  if (!champ) return <div style={{ width: size, height: size }} className="bg-zinc-800 shrink-0" />;
  return (
    <img
      src={getChampionIconUrl(champ.id, version)}
      alt={champ.name}
      style={{ width: size, height: size }}
      className="object-cover shrink-0"
      onError={(e) => { e.currentTarget.style.display = 'none'; }}
    />
  );
};

export default function GameList({ games, onDelete, onEdit, onAIAnalyze, aiAnalyses, userSettings, champions, version, lpDeltasByGame }) {
  if (games.length === 0) {
    return <EmptyState title="Aucune game enregistrée" body="Importe tes games depuis Riot" size="lg" />;
  }

  const hasAnalysis = (gameId) => aiAnalyses?.some(a => a.game_id === gameId && a.response_text);

  return (
    <div className="space-y-2">
      {[...games].reverse().map(g => (
        <div key={g.id} className="group border border-zinc-800 hover:border-zinc-700 transition bg-zinc-950 hover:bg-zinc-950/50">
          <div className="flex items-center gap-2 md:gap-3 p-2 md:p-3">
            <div className={`w-1 h-10 md:h-12 ${g.result === 'W' ? 'bg-gradient-to-b from-cyan-400 to-cyan-600' : 'bg-gradient-to-b from-fuchsia-500 to-rose-600'}`} />
            <div className={`font-black text-xl md:text-2xl w-6 md:w-8 ${g.result === 'W' ? 'text-cyan-400' : 'text-fuchsia-400'}`}>{g.result}</div>
            <ChampIcon name={g.champion} champions={champions} version={version} size={36} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 md:gap-2 flex-wrap">
                <div className="font-bold text-xs md:text-sm truncate">{g.champion}</div>
                {g.isAutofill && (
                  <span className="text-[8px] md:text-[9px] font-bold tracking-wider px-1.5 py-0.5 border border-amber-400/50 bg-amber-400/10 text-amber-400 flex-shrink-0">
                    AUTOFILL{g.role && g.role !== 'ADC' ? ` ${g.role}` : ''}
                  </span>
                )}
                <span className="text-zinc-600 text-xs">vs</span>
                <ChampIcon name={g.enemyAdc} champions={champions} version={version} size={18} />
                <div className="text-[11px] md:text-xs text-zinc-400 truncate">{g.enemyAdc}</div>
              </div>
              <div className="text-[10px] text-zinc-500 font-mono mt-0.5">
                +{g.support} · {g.duration}min · {g.csPerMin}cs
              </div>
            </div>
            <div className="font-mono text-xs md:text-sm text-right">
              <span className="text-cyan-400">{g.kills}</span>/<span className="text-rose-400">{g.deaths}</span>/<span className="text-zinc-400">{g.assists}</span>
              <div className="text-[10px] text-zinc-500">{g.csPerMin} cs · {g.duration}min</div>
            </div>
            {/* LP delta */}
            {(() => {
              const lpInfo = lpDeltasByGame?.get(g.id);
              if (!lpInfo || lpInfo.delta === null) return null;
              const isWin = lpInfo.delta > 0;
              const isZero = lpInfo.delta === 0;
              const colorClasses = isWin
                ? 'border-cyan-400/60 bg-cyan-400/15 text-cyan-300'
                : isZero ? 'border-zinc-700 bg-zinc-900 text-zinc-500'
                : 'border-rose-400/60 bg-rose-400/15 text-rose-300';
              return (
                <div className={`hidden md:flex flex-col items-center justify-center w-16 px-2 py-1.5 border ${colorClasses}`}>
                  <div className="font-mono font-black text-base leading-none">{isWin ? '+' : ''}{lpInfo.delta}</div>
                  <div className="text-[8px] tracking-[0.2em] uppercase font-bold mt-0.5 opacity-70">LP</div>
                </div>
              );
            })()}
            {/* Death details */}
            {g.deathDetails && g.deathDetails.length > 0 && (
              <div className="hidden md:flex flex-col gap-0.5">
                {Object.entries(DEATH_TYPES).map(([k, v]) => {
                  const count = g.deathDetails.filter(d => d.type === k).length;
                  if (count === 0) return null;
                  return (
                    <span key={k} className={`text-[9px] font-bold px-1.5 py-0.5 border ${v.borderClass} ${v.textClass}`}>
                      {count} {v.label}
                    </span>
                  );
                })}
              </div>
            )}
            {/* Actions */}
            <div className="hidden md:flex gap-1 opacity-0 group-hover:opacity-100 transition">
              {onAIAnalyze && (
                <button onClick={() => onAIAnalyze(g)}
                  className={`text-[9px] px-2 py-1 border transition font-bold ${hasAnalysis(g.id) ? 'border-fuchsia-400/50 text-fuchsia-400 hover:bg-fuchsia-400/10' : 'border-zinc-700 text-zinc-500 hover:text-zinc-300'}`}>
                  IA
                </button>
              )}
              {onEdit && (
                <button onClick={() => onEdit(g)}
                  className="text-[9px] px-2 py-1 border border-zinc-700 text-zinc-500 hover:text-zinc-300 transition font-bold">
                  ✎
                </button>
              )}
              {onDelete && (
                <button onClick={() => onDelete(g.id)}
                  className="text-[9px] px-2 py-1 border border-zinc-700 text-zinc-600 hover:text-rose-400 hover:border-rose-400/50 transition">
                  ✕
                </button>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
